import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { useRouter } from '@/hooks';
import Box from '@mui/material/Box';
import { Autocomplete, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { PoiIcon } from '@/components/Analysis/modules/pointOfInterest/PointOfInterestTab/PoiIcon';
import {
  useAllLocationsForAssemblyForDeckPlanLazyQuery,
  useAllPaintRegionsForDeckPlanQuery,
} from '@/__generated__/graphql';
import * as analysisState from '@/components/Analysis/state';
import {
  Overlay,
  OverlayImage,
  zoomButtonStyles,
  HeatmapSvg,
  PaintRegionSvg,
  ZoomButtonsBoxStyles,
  ErrorContainer,
  SphericalButton,
} from './styles';
import { ColorLegend } from '../ColorLegend';
import { POI_DETAILS_DEFAULT, POI_DETAILS_FALLBACK } from '@/constants';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { mapLocationToCurrentImage } from '../../Viewer/hooks/useImageFromLocationId';
import { upperSnakeCase } from '@/utils';
import { abyssColors } from '@/theme/colors';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { PageLoader } from '@/components/shared/PageLoader';

type Polygon = {
  id: string;
  points: number[][];
  type: 'paintRegion' | 'cameraPosition';
  svg: string;
  color: string;
  strokeColor: string;
  strokeWidth: number;
};

type Props = {
  deckId: string;
  resourcePath: string;
  deckName: string;
  handleSetLoading: (loading: boolean) => void;
  handleFallbackImage: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  imageError?: boolean;
  loading: boolean;
};

const makeStyle = (color: string, strokeColor: string, strokeWidth: number | string) => ({
  fill: color,
  stroke: strokeColor || 'black',
  strokeWidth: strokeWidth || 1,
  cursor: 'pointer',
});

const calculateStrokeWidth = (viewBoxW: number) => {
  // Dynamic formula to calculate stroke width based on viewBox width
  const slope = 0.0028758;
  const intercept = -3.33;

  // Calculate the stroke width using the formula
  const strokeWidth = slope * viewBoxW + intercept;

  // Ensure the stroke width is not below a minimum value, e.g., 1
  return Math.max(1, strokeWidth);
};

export const DeckPlanOverlay = ({
  deckId,
  resourcePath,
  deckName,
  imageError = false,
  handleSetLoading,
  handleFallbackImage,
  loading,
}: Props) => {
  const router = useRouter();

  const olimage = useRef<HTMLImageElement | null>(null);
  const zoomContainer = useRef<HTMLImageElement | null>(null);
  const selectedTemplateFilters = useAtomValue(analysisState.selectedTemplateFiltersState);

  const showPaintRegions = useAtomValue(analysisState.showPaintRegionsState);
  const [showCameraPositions, setShowCameraPositions] = useAtom(
    analysisState.showCameraPositionsState
  );
  const markerScaleValue = useAtomValue(analysisState.markerScaleSize);
  const poiTemplates = useAtomValue(analysisState.pointOfInterestTemplates);

  const [overlay, setOverlay] = useState({
    viewBoxW: 1494,
    viewBoxH: 902,
  });
  const [scale, setScale] = useState(1);
  const [poiScale, setPOIScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [lastCords, setLastCords] = useState({ x: 0, y: 0 });
  const [selectedCameraPosition, setSelectedCameraPosition] = useState<string | undefined>('');
  const [selectedPaintRegion, setSelectedPaintRegion] = useState<string | undefined>('');

  const heatmapSource = useAtomValue(analysisState.heatmapSvgState);
  const paintRegionSource = useAtomValue(analysisState.paintRegionOutline);

  const setSelectedAssemblyId = useSetAtom(analysisState.selectedAssemblyId);
  const setSelectedSpherical = useSetAtom(analysisState.selectedSpherical);
  const [applyFilters, setApplyFilters] = useAtom(analysisState.applyFilters);
  const poiTemplateIcons = useAtomValue(analysisState.poiTemplateIcons);
  const structureId = useGetStructureId();

  const [
    allLocationsForAssemblyForDeckPlanQuery,
    { loading: allLocationsLoading, data: cameraPositionsData },
  ] = useAllLocationsForAssemblyForDeckPlanLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const previousDeckId = useRef(deckId);

  useEffect(() => {
    if ((deckId && deckId !== previousDeckId.current) || applyFilters) {
      allLocationsForAssemblyForDeckPlanQuery({
        variables: {
          input: {
            structureId,
            deckId,
            selectedTemplateFilters,
          },
        },
      });
      setApplyFilters(false);
      previousDeckId.current = deckId; // Update the ref with the new deckId
    }
  }, [
    deckId,
    applyFilters,
    structureId,
    selectedTemplateFilters,
    allLocationsForAssemblyForDeckPlanQuery,
    setApplyFilters,
  ]);

  const { data: paintRegionsData } = useAllPaintRegionsForDeckPlanQuery({
    variables: {
      deckIds: [deckId],
    },
    fetchPolicy: 'no-cache',
  });

  const allPolygons = useCallback((polygons: Polygon[]) => {
    if (polygons.length > 0) {
      return polygons.map((polygon) => ({
        ...polygon,
        svg: polygon.points.flat(10).join(','),
      }));
    }
    return [];
  }, []);

  const cameraPositionsPolygons = useMemo(() => {
    if (cameraPositionsData?.allLocationsForAssembly && showCameraPositions) {
      const { allLocationsForAssembly } = cameraPositionsData;

      if (allLocationsForAssembly) {
        const allCameraPositionsPolygons = allLocationsForAssembly.map(({ name, deckPlan }) => {
          let points: Array<Array<number>> = [];
          if (deckPlan) {
            const [p1, p2] = deckPlan.coordinates;
            if (deckPlan.coordinates.length === 0) {
              setShowCameraPositions(false);
            } else {
              points = [
                [p1 - markerScaleValue, p2 - markerScaleValue], // top left
                [p1 + markerScaleValue, p2 - markerScaleValue], // top right
                [p1 + markerScaleValue, p2 + markerScaleValue], // bottom right
                [p1 - markerScaleValue, p2 + markerScaleValue], // bottom left
              ];
            }
          }
          const viewBoxW = overlay?.viewBoxW;
          const dynamicStrokeWidth = calculateStrokeWidth(viewBoxW);
          return {
            id: name,
            points,
            color: '#9A8ED980',
            strokeColor: '#6250BA',
            strokeWidth: dynamicStrokeWidth || 4,
            svg: resourcePath,
            type: 'cameraPosition',
          } as Polygon;
        });

        return allPolygons(allCameraPositionsPolygons);
      }
    }
    return [];
  }, [
    cameraPositionsData,
    showCameraPositions,
    allPolygons,
    resourcePath,
    setShowCameraPositions,
    markerScaleValue,
    overlay,
  ]);

  const paintRegionPolygons = useMemo(() => {
    if (paintRegionsData?.allPaintRegions && showPaintRegions) {
      const { allPaintRegions } = paintRegionsData;

      if (allPaintRegions) {
        const allPaintRegionsPolygons = allPaintRegions.map(({ name, polygons }) => {
          const { points } = polygons[0];
          return {
            id: name,
            points,
            color: 'rgba(174, 0, 255, 0.5)',
            strokeColor: 'black',
            strokeWidth: 4,
            type: 'paintRegion',
          } as Polygon;
        });

        return allPolygons(allPaintRegionsPolygons);
      }
    }
    return [];
  }, [allPolygons, paintRegionsData, showPaintRegions]);

  const spatialPolygons = useMemo(() => {
    switch (true) {
      case showPaintRegions && showCameraPositions:
        return [...paintRegionPolygons, ...cameraPositionsPolygons];
      case showPaintRegions:
        return paintRegionPolygons;
      case showCameraPositions:
        return cameraPositionsPolygons;
      default:
        return [];
    }
  }, [cameraPositionsPolygons, paintRegionPolygons, showCameraPositions, showPaintRegions]);

  useEffect(() => {
    handleSetLoading(allLocationsLoading);
  }, [handleSetLoading, allLocationsLoading]);

  const loaded = useCallback(() => {
    if (olimage.current) {
      setOverlay({
        viewBoxW: olimage.current.naturalWidth,
        viewBoxH: olimage.current.naturalHeight,
      });
    }
  }, []);

  useEffect(() => {
    loaded();
    setScale(1);
  }, [loaded, olimage]);

  const imageTransform = useMemo(() => {
    return {
      transform: `scale(${scale}) translate(${lastCords.x}px,${lastCords.y}px)`,
    };
  }, [lastCords.x, lastCords.y, scale]);

  const calculateViewBox = useMemo(() => {
    return `0 0 ${overlay.viewBoxW} ${overlay.viewBoxH}`;
  }, [overlay]);

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const { movementX: deltaX, movementY: deltaY } = event;
    const zoomReference = zoomContainer.current;
    if (zoomReference) {
      const { width, height } = zoomReference.getBoundingClientRect();
      const xLimit = width / (6 - scale);
      const yLimit = height / (6 - scale);
      const newX = Math.max(Math.min(lastCords.x + deltaX, xLimit), -xLimit);
      const newY = Math.max(Math.min(lastCords.y + deltaY, yLimit), -yLimit);
      setLastCords({ ...lastCords, x: newX, y: newY });
    }
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const zoomOut = useCallback(() => {
    if (scale > 1) {
      setScale(scale - 1);
    }
  }, [setScale, scale]);

  const zoomIn = useCallback(() => {
    if (scale < 4) {
      setScale(scale + 1);
    }
  }, [setScale, scale]);

  const cameraPositionsNames = useMemo(() => {
    if (!cameraPositionsData?.allLocationsForAssembly) return [];
    return cameraPositionsData?.allLocationsForAssembly.map(
      (cameraPosition) => cameraPosition.name
    );
  }, [cameraPositionsData]);

  const paintRegionsNames = useMemo(() => {
    if (!paintRegionsData?.allPaintRegions) return [];
    return paintRegionsData?.allPaintRegions.map((paintRegion) => paintRegion.name);
  }, [paintRegionsData]);

  const polygonHover = useCallback(
    (event: React.MouseEvent, id: string, type: string) => {
      if (type === 'cameraPosition') {
        setSelectedCameraPosition(
          cameraPositionsNames.find((cameraPositionId) => cameraPositionId === id)
        );
      }

      if (type === 'paintRegion') {
        setSelectedPaintRegion(paintRegionsNames.find((paintRegionId) => paintRegionId === id));
      }

      event.preventDefault();
    },
    [cameraPositionsNames, paintRegionsNames]
  );

  const navigateToImage = useCallback(
    (event: React.MouseEvent, name: string) => {
      setSelectedAssemblyId(undefined);

      const selectedViewpoint = cameraPositionsData?.allLocationsForAssembly?.find(
        (viewpoint) => viewpoint.name === name
      );
      if (selectedViewpoint?.pose) {
        const viewpoint = {
          id: selectedViewpoint.id,
          name: selectedViewpoint.name,
          resourcePath: selectedViewpoint.resourcePath,
          distance: selectedViewpoint?.distance,
          pose: selectedViewpoint.pose,
          imageSensor: selectedViewpoint.imageSensor,
          deck: selectedViewpoint.deck,
        };
        setSelectedSpherical(mapLocationToCurrentImage(viewpoint));
      }

      event.preventDefault();

      router.push({
        pathname: `/analysis/${structureId}/viewer`,
        query: `?selectedSpherical=${selectedViewpoint?.name}`,
      });
    },
    [
      cameraPositionsData?.allLocationsForAssembly,
      router,
      setSelectedAssemblyId,
      setSelectedSpherical,
      structureId,
    ]
  );

  const polygonClicked = useCallback(
    (event: React.MouseEvent, name: string, type: string) => {
      if (type === 'cameraPosition') {
        navigateToImage(event, name);
      }
    },
    [navigateToImage]
  );

  const handleHighlightPolygonChange = useCallback(
    (event: React.SyntheticEvent<Element, Event>, value: string | null) => {
      setSelectedCameraPosition(cameraPositionsNames.find((poly) => value?.match(poly)));
    },
    [cameraPositionsNames]
  );

  const activePolygon = useMemo<Polygon | undefined>(() => {
    if (!cameraPositionsPolygons || !selectedCameraPosition) return undefined;
    return cameraPositionsPolygons.find(({ id }) => id === selectedCameraPosition);
  }, [cameraPositionsPolygons, selectedCameraPosition]);

  const drawCrosshair = useMemo(() => {
    if (cameraPositionsPolygons && selectedCameraPosition && activePolygon) return true;
    // do not draw crosshair when no active polygon
    return false;
  }, [activePolygon, cameraPositionsPolygons, selectedCameraPosition]);

  const crosshair = useMemo(() => {
    if (!drawCrosshair) return { x: 0, y: 0 };
    // return active polygon centroid coordinates

    if (activePolygon === undefined) return { x: 0, y: 0 };

    const { points } = activePolygon;

    const getCenterPointInAxis = (pts: Array<number>) => (Math.min(...pts) + Math.max(...pts)) / 2;
    const range = points.reduce<{ x: Array<number>; y: Array<number> }>(
      ({ x, y }, [pt1, pt2]) => ({
        x: [...x, pt1],
        y: [...y, pt2],
      }),
      { x: [], y: [] }
    );
    return {
      x: getCenterPointInAxis(range.x),
      y: getCenterPointInAxis(range.y),
    };
  }, [activePolygon, drawCrosshair]);

  const calculateCrosshairStyle: React.CSSProperties = useMemo(() => {
    const x = Math.hypot(0.0025 * overlay.viewBoxW, 0.0025 * overlay.viewBoxH);
    return { stroke: 'rgb(255,0,0)', strokeWidth: 0.5 * x };
  }, [overlay]);

  useEffect(() => {
    if (olimage.current) {
      const initialPOIScale = 0.4;
      const planWidth = olimage.current.naturalWidth;
      const resizeFactor = planWidth / 1000;
      const relativeScale = initialPOIScale * resizeFactor;
      setPOIScale(scale / relativeScale);
    }
  }, [scale, olimage, poiTemplates]);

  if (loading || allLocationsLoading) return <PageLoader />;

  return (
    <Paper elevation={3} sx={{ marginTop: '32px', position: 'relative' }}>
      <Box sx={{ m: 4, pt: 3 }}>
        <div data-testid="deck-overlay">
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body2" fontWeight="700" color={abyssColors.primary[400]}>
                  Deck map:
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight="bold"
                    fontSize="1.4rem"
                    sx={{ color: abyssColors.primary[600], marginLeft: 1 }}
                  >
                    {deckName}
                  </Typography>
                </Typography>
                <Typography variant="body2" fontWeight="700" color={abyssColors.primary[400]}>
                  Total scan count:
                  <Typography
                    component="span"
                    variant="body2"
                    fontWeight="bold"
                    fontSize="1.4rem"
                    sx={{ color: abyssColors.primary[600], marginLeft: '5px' }}
                  >
                    {cameraPositionsPolygons.length}
                  </Typography>
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                {showPaintRegions && (
                  <TextField
                    disabled
                    id="standard-basic"
                    label="Paint Region"
                    variant="standard"
                    value={selectedPaintRegion}
                  />
                )}
                <Stack direction="row" alignItems="flex-end" gap={2}>
                  <Autocomplete
                    sx={{ width: '200px' }}
                    id="auto-complete"
                    options={allLocationsLoading ? [] : [...cameraPositionsNames, '']}
                    getOptionLabel={(option) => option}
                    value={selectedCameraPosition}
                    onChange={handleHighlightPolygonChange}
                    autoComplete
                    includeInputInList
                    renderInput={(parameters) => (
                      <TextField {...parameters} label="Spherical" variant="standard" />
                    )}
                    loading={allLocationsLoading}
                    noOptionsText={
                      !allLocationsLoading && cameraPositionsNames.length === 0 && 'No data found'
                    }
                  />
                  {selectedCameraPosition && (
                    <SphericalButton
                      onClick={(event) => {
                        navigateToImage(event, selectedCameraPosition);
                      }}
                    >
                      <AbyssIcon
                        iconName="image-filled"
                        style={{ width: '16px', height: '16px' }}
                      />
                    </SphericalButton>
                  )}
                </Stack>
              </Box>
            </Box>
            <Overlay
              ref={zoomContainer}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
            >
              {deckId && (
                <>
                  <div style={imageTransform}>
                    <OverlayImage
                      ref={olimage}
                      src={getCloudfrontUrl(resourcePath)}
                      crossOrigin="use-credentials"
                      alt="deck selected"
                      onLoad={loaded}
                      onError={handleFallbackImage}
                    />
                    {imageError ? (
                      <ErrorContainer>
                        <img src="/assets/fallback_deckplan.svg" alt="group icon" />
                        <Typography variant="body2" fontWeight={400}>
                          Image failed to load. You can{' '}
                          <Link
                            href="#"
                            onClick={() => {
                              window.location.reload();
                            }}
                            color="#165DFF"
                          >
                            refresh
                          </Link>{' '}
                          the page to try again.
                        </Typography>
                      </ErrorContainer>
                    ) : (
                      <>
                        {heatmapSource && (
                          <HeatmapSvg
                            alt="heatmap"
                            src={`data:image/svg+xml;utf8,${heatmapSource}`}
                          />
                        )}
                        {paintRegionSource && (
                          <PaintRegionSvg
                            alt="paintRegion"
                            src={`data:image/svg+xml;utf8,${paintRegionSource}`}
                          />
                        )}
                        <svg viewBox={calculateViewBox}>
                          {drawCrosshair && (
                            <>
                              <line
                                x1={crosshair.x}
                                y1="0"
                                x2={crosshair.x}
                                y2="99999"
                                style={calculateCrosshairStyle}
                              />
                              <line
                                x1="0"
                                y1={crosshair.y}
                                x2="9999"
                                y2={crosshair.y}
                                style={calculateCrosshairStyle}
                              />
                            </>
                          )}
                          {(showPaintRegions || showCameraPositions) &&
                            spatialPolygons?.map((polygon) => (
                              <polygon
                                data-testid={`polygon-${polygon.id}`}
                                key={polygon.id}
                                id={polygon.id}
                                points={polygon.svg}
                                onMouseOver={(event) =>
                                  polygonHover(event, polygon.id, polygon.type)
                                }
                                onClick={(event) => polygonClicked(event, polygon.id, polygon.type)}
                                style={makeStyle(
                                  polygon.color,
                                  polygon.strokeColor,
                                  polygon.strokeWidth
                                )}
                              />
                            ))}
                          {poiTemplates?.map(({ name, pointOfInterests }) => (
                            <>
                              {pointOfInterests.map(({ location }) => {
                                // Checking for location, in case POI was not created
                                // via the 3D viewer, in that case it wont have any 3D context
                                if (!location) return <></>;
                                const { cx, cy } = location;
                                return (
                                  <>
                                    <circle
                                      cx={cx + 8.5 / poiScale}
                                      cy={cy + 8 / poiScale}
                                      r={24 / poiScale}
                                      fill={`#${
                                        poiTemplateIcons
                                          .find(
                                            (template) =>
                                              upperSnakeCase(template.name) === upperSnakeCase(name)
                                          )
                                          ?.color?.replace('#', '') ??
                                        POI_DETAILS_FALLBACK.find(
                                          (fallbackDetail) =>
                                            fallbackDetail.name === upperSnakeCase(name)
                                        )?.iconColor ??
                                        POI_DETAILS_DEFAULT.iconColor
                                      }`}
                                      stroke="#000"
                                      strokeWidth={1 / poiScale}
                                      style={{
                                        mixBlendMode: 'normal',
                                        opacity: 0.7,
                                      }}
                                    />
                                    <svg
                                      width={17 / poiScale}
                                      height={17 / poiScale}
                                      stroke="#fff"
                                      color="#fff"
                                      x={cx}
                                      y={cy}
                                    >
                                      <PoiIcon name={name} />
                                    </svg>
                                  </>
                                );
                              })}
                            </>
                          ))}
                        </svg>
                      </>
                    )}
                  </div>
                  {!imageError && (
                    <Box sx={ZoomButtonsBoxStyles}>
                      <IconButton
                        sx={zoomButtonStyles}
                        style={{ marginRight: '5px' }}
                        color="primary"
                        onClick={zoomOut}
                      >
                        <ZoomOut sx={{ color: '#fff' }} />
                      </IconButton>
                      <Divider orientation="vertical" flexItem color="#fff" />
                      <IconButton sx={zoomButtonStyles} color="primary" onClick={zoomIn}>
                        <ZoomIn sx={{ color: '#fff' }} />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}
            </Overlay>
            <ColorLegend sxProps={{ marginTop: 0, marginBottom: '8px' }} />
          </Box>
        </div>
      </Box>
    </Paper>
  );
};
